import type { RxJsonSchema } from 'rxdb'

/**
 * TaskRelation Document Type (relation/link between two tasks)
 *
 * For directional relations, source is the "active" side:
 * - predecessor_successor: source = predecessor, target = successor
 * - blocks_blocked_by: source = blocker, target = blocked
 * - parent_child: source = parent, target = child
 * - related_to / duplicates: symmetric (order doesn't matter)
 */
export interface TaskRelationDocument {
  id: string
  envId: string
  sourceTaskId: string
  sourceProjectId?: string | null
  targetTaskId: string
  targetProjectId?: string | null
  relationType: number // 0=related_to, 1=predecessor_successor, 2=blocks_blocked_by, 3=parent_child, 4=duplicates
  isDeleted: boolean

  // Denormalized for offline display
  sourceTaskName?: string
  sourceIdentifier?: string | null
  targetTaskName?: string
  targetIdentifier?: string | null

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for TaskRelation
 */
export const taskRelationSchema: RxJsonSchema<TaskRelationDocument> = {
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    sourceTaskId: {
      type: 'string',
      maxLength: 36,
    },
    sourceProjectId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    targetTaskId: {
      type: 'string',
      maxLength: 36,
    },
    targetProjectId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    relationType: {
      type: 'number',
      minimum: 0,
      maximum: 4,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Denormalized fields
    sourceTaskName: {
      type: 'string',
      maxLength: 500,
      default: '',
    },
    sourceIdentifier: {
      type: ['string', 'null'],
      maxLength: 50,
    },
    targetTaskName: {
      type: 'string',
      maxLength: 500,
      default: '',
    },
    targetIdentifier: {
      type: ['string', 'null'],
      maxLength: 50,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'sourceTaskId',
    'targetTaskId',
    'relationType',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'sourceTaskId',
    'targetTaskId',
    'envId',
    'updatedOn',
    ['sourceTaskId', 'isDeleted'],
    ['targetTaskId', 'isDeleted'],
  ],
}
